import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/YoutubeCmsChannelDetail.graphql';
import { Query, QueryyoutubeCmsChannelDetailArgs } from '../../__generated__/globalTypes';

export const useYoutubeCmsChannelDetailQuery = (
  options?: QueryHookOptions<Query, QueryyoutubeCmsChannelDetailArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryyoutubeCmsChannelDetailArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useYoutubeCmsChannelDetailLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryyoutubeCmsChannelDetailArgs>
): QueryResult<Query, QueryyoutubeCmsChannelDetailArgs> & {
  getYoutubeCmsChannelDetail: LazyQueryExecFunction<Query, QueryyoutubeCmsChannelDetailArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getYoutubeCmsChannelDetail, result] = useLazyQuery<Query, QueryyoutubeCmsChannelDetailArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getYoutubeCmsChannelDetail, ...result };
};

export const useYoutubeCmsChannelDetailPromiseQuery = () => {
  const client = useApolloClient();

  const getYoutubeCmsChannelDetail = useCallback(
    (options?: Omit<QueryOptions<QueryyoutubeCmsChannelDetailArgs, Query>, 'query'>) =>
      client.query<Query, QueryyoutubeCmsChannelDetailArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getYoutubeCmsChannelDetail };
};
// Suspense Query Hooks
export const useYoutubeCmsChannelDetailSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryyoutubeCmsChannelDetailArgs>
) => {
  const result = useSuspenseQuery<Query, QueryyoutubeCmsChannelDetailArgs>(QUERY, {
    ...options
  });

  return result;
};
